import { useEffect, useState, useRef, useCallback } from "react";
import {
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Popover,
  List,
  Checkbox,
  Form,
  Modal,
  Tag,
  Switch,
  Drawer,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  LockOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  ExportOutlined,
} from "@ant-design/icons";

import { debounce } from "throttle-debounce";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { TITLE, BASE_PATH } from ".";
import service from "../../services/order";
import { TbColumns3 } from "react-icons/tb";
import warehouseService from "../../services/warehouse";
import settingsService from "../../services/settings";
import useSearchParamsTableState from "../../hooks/useSearchParamsTableState";
import { useSelector } from "react-redux";
import { Store } from "../../store";
import OrderItemsModal from "../../components/OrderItemsModal";
import useSelectedColumnsState from "../../hooks/useSelectedColumnsState";
import mappingColumns from "../../utils/mappingColumns";

const { Text, Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const MASTER_KEYS = [
  {
    value: "warehouse",
    key: "warehouse",
    service: warehouseService,
  },
  {
    value: "season",
    key: "season",
    service: { getAll: settingsService.getSeasons },
  },
  {
    value: "channel",
    key: "channel",
    service: { getAll: settingsService.getChannels },
  },
  {
    value: "brand",
    key: "brand",
    service: { getAll: settingsService.getBrands },
  },
  {
    value: "category",
    key: "category",
    service: { getAll: settingsService.getCategories },
  },
];
const ADDITIONAL_COLUMNS = mappingColumns;

export default function Entities(props: any) {
  const breadcrumbsRef = useRef([{ label: TITLE[1], url: BASE_PATH }]);
  const [input, setInput, tableState, setTableState, data, loading, onRefresh] =
    useSearchParamsTableState({ onFetch: service.paginateItems });
  const navigate = useNavigate();
  const [resetPasswordVisible, setResetPasswordVisible] = useState("");
  const [toggleLoading, setToggleLoading] = useState(false);
  const [toggleKey, setToggleKey] = useState("");
  const [selectedClient, setSelectedClient] = useState<any>();
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const warehouseState = useSelector((state: Store) => state.warehouse);
  const [columnsDrawerVisible, setColumnsDrawerVisible] = useState(false);
  const clientState = useSelector((state: Store) => state.client);

  const { selectedColumns, setSelectedColumns } = useSelectedColumnsState({
    breadcrumbs: breadcrumbsRef.current,
    availableColumns: ADDITIONAL_COLUMNS,
    defaultSelected: [],
  });
  useEffect(() => {
    loadMastersDebounced();
  }, []);
  const [masters, setMasters] = useState<any>({});
  useEffect(() => {
    if (tableState) {
      setTableState({
        ...tableState,
        filters: {
          ...tableState.filters,
          warehouse: [warehouseState.selected],
        },
      });
    }
  }, [warehouseState.selected]);
  const getColumnsFilters = useCallback(
    (key: string, idAccessor = "_id") => {
      return masters[key]
        ? masters[key].map((entity) => ({
            text: entity.name,
            value: entity[idAccessor],
          }))
        : [];
    },
    [masters]
  );
  const loadMasters = useCallback(() => {
    const promises: any[] = [];
    for (let masterKey of MASTER_KEYS) {
      promises.push(
        masterKey.service
          .getAll()
          .then((p) => ({ key: masterKey.value, data: p }))
      );
    }
    Promise.all(promises).then((payload) => {
      const output: any = {};
      payload.forEach((m) => (output[m.key] = m.data));
      setMasters(output);
    });
  }, []);
  const loadMastersDebounced = useCallback(debounce(300, loadMasters), []);

  const columns: any[] = [
    { title: "SKU Code", dataIndex: "skuCode", key: "skuCode" },
    { title: "Quantity", dataIndex: "qty", key: "qty" },
    {
      title: "Selling Price Per Item",
      dataIndex: "sellingPricePerItem",
      key: "sellingPricePerItem",
    },

    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
    },
    { title: "Tax Rate", dataIndex: "taxRate", key: "taxRate" },
    { title: "Tax Amount", dataIndex: "taxAmount", key: "taxAmount" },

    { title: "Status", dataIndex: "status", key: "status" },

    ...selectedColumns.map((c) => ({
      ...c,
      filters: masters[c.key] ? getColumnsFilters(c.key, "name") : undefined,
    })),
  ];
  const handleView = (entityId?: string) => {
    navigate(`${BASE_PATH}/${entityId}`);
  };

  return (
    <div>
      <Drawer
        open={columnsDrawerVisible}
        title="Select Columns"
        onClose={() => setColumnsDrawerVisible(false)}
      >
        <List
          size="small"
          dataSource={ADDITIONAL_COLUMNS}
          renderItem={(item) => (
            <List.Item className="flex items-center gap-x-2 justify-start">
              <div className="w-max">
                <Checkbox
                  checked={selectedColumns.some((c) => c.key === item.key)}
                  onChange={(e) => {
                    setSelectedColumns(item);
                  }}
                  children={item.title}
                />
              </div>
            </List.Item>
          )}
        />
      </Drawer>
      <Row justify="space-between">
        <Col>
          <Title level={3}>All Order Items</Title>
        </Col>
        <Col></Col>
        <Col>
          <Button
            icon={<TbColumns3 />}
            onClick={() => setColumnsDrawerVisible(true)}
          />
        </Col>
      </Row>
      {/* <Row justify="space-between" className="p-4">
        <Col className="flex">
          <Search
            placeholder={`Search ${TITLE[1]}`}
            enterButton={<SearchOutlined />}
            size="middle"
            onChange={(event) => {
              setInput(event.target.value);
            }}
            value={input}
          />
        </Col>
        <Col>
          {data?.queryCount !== data?.totalCount && (
            <Text className="flex items-center">
              Showing {data.queryCount} of {data?.totalCount} entries
              <Button
                type="link"
                onClick={() => {
                  setInput("");
                }}
              >
                Clear Search
              </Button>
            </Text>
          )}
        </Col>
        <Col></Col>
      </Row> */}
      <Row className="mt-2">
        <Col span={24}>
          <div className="overflow-data-table">
            <Table
              loading={loading}
              dataSource={data?.list}
              columns={columns}
              pagination={{
                ...(tableState?.pagination || {}),
                total: data?.queryCount,
              }}
              bordered
              className="overflow-table"
              onChange={(pagination, filters, sorter) => {
                delete (sorter as any).column;
                setTableState({
                  pagination,
                  filters: { ...filters, client: [clientState.current] },
                  sorter,
                });
                setTableState({ pagination, filters, sorter });
              }}
              rowKey={(record) => record._id}
              size="small"
            />
          </div>
        </Col>
      </Row>
      <OrderItemsModal
        open={!!selectedEntity}
        data={selectedEntity}
        type="invoice"
        onCancel={() => setSelectedEntity(undefined)}
      />
    </div>
  );
}
